import { 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Draft document structure
export interface RegistrationDraft {
  eventId: string;
  odId: string;
  formData: Record<string, any>;
  currentStep: number;
  visitedSteps: number[];
  lastModified: Timestamp;
  createdAt: Timestamp;
  
  // Analytics metadata
  analytics: {
    navigationHistory: { from: number; to: number; timestamp: Timestamp }[];
    validationErrorCount: number;
    sessionCount: number;
    totalTimeSpentMs: number;
    currentSessionStartedAt: Timestamp;
  };
}

// Generate document ID from eventId and odId
const getDraftDocId = (eventId: string, odId: string): string => {
  return `${eventId}_${odId}`;
};

/**
 * Sanitize form data for Firestore - replace undefined with null
 * Firestore does not accept undefined values
 */
const sanitizeForFirestore = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value === undefined) {
      result[key] = null;
    } else if (value instanceof Date) {
      result[key] = value.toISOString();
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = sanitizeForFirestore(value);
    } else {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Save a registration draft to Firestore
 */
export const saveDraft = async (
  eventId: string, 
  odId: string, 
  formData: Record<string, any>,
  currentStep: number,
  visitedSteps: number[],
  analytics: RegistrationDraft['analytics']
): Promise<void> => {
  const docId = getDraftDocId(eventId, odId);
  const draftRef = doc(db, 'registrationDrafts', docId);
  
  // Sanitize formData: convert undefined to null and Date to ISO string
  const serializedFormData = sanitizeForFirestore(formData);
  
  // Check if draft exists to preserve createdAt
  const existingDoc = await getDoc(draftRef);
  const createdAt = existingDoc.exists() 
    ? existingDoc.data().createdAt 
    : serverTimestamp();
  
  await setDoc(draftRef, {
    eventId,
    odId,
    formData: serializedFormData,
    currentStep,
    visitedSteps,
    lastModified: serverTimestamp(),
    createdAt,
    analytics
  });
};

/**
 * Load a registration draft from Firestore
 */
export const loadDraft = async (
  eventId: string, 
  odId: string
): Promise<RegistrationDraft | null> => {
  const docId = getDraftDocId(eventId, odId);
  const draftRef = doc(db, 'registrationDrafts', docId);
  
  const docSnap = await getDoc(draftRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  const data = docSnap.data() as RegistrationDraft;
  
  // Convert ISO date strings back to Date objects
  if (data.formData.dateOfBirth && typeof data.formData.dateOfBirth === 'string') {
    data.formData.dateOfBirth = new Date(data.formData.dateOfBirth);
  }
  if (data.formData.waitinglistExpires && typeof data.formData.waitinglistExpires === 'string') {
    data.formData.waitinglistExpires = new Date(data.formData.waitinglistExpires);
  }
  
  return data;
};

/**
 * Delete a registration draft from Firestore
 * Called when a registration is successfully submitted
 */
export const deleteDraft = async (eventId: string, odId: string): Promise<void> => {
  const docId = getDraftDocId(eventId, odId);
  const draftRef = doc(db, 'registrationDrafts', docId);
  
  await deleteDoc(draftRef);
};

/**
 * Check if a draft exists without loading full data
 */
export const draftExists = async (eventId: string, odId: string): Promise<boolean> => {
  const docId = getDraftDocId(eventId, odId);
  const draftRef = doc(db, 'registrationDrafts', docId);
  
  const docSnap = await getDoc(draftRef);
  return docSnap.exists();
};
